import {
  formattedUTCDay,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  Text,
  Tooltip,
  type Column,
  type ColumnDef,
  type SizeColumnParams,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { ACTION_METADATA, type ImportCareOrderAction, type ImportedCareOrder } from './types';

interface UseCareOrderImportColumnsParams {
  onClickAction: (row: ImportedCareOrder) => void;
}

export const useCareOrderImportColumnsAladdin = ({ onClickAction }: UseCareOrderImportColumnsParams) => {
  const columns: Column[] = useMemo(() => {
    return [
      getActionColumn(onClickAction),
      {
        type: 'security',
        field: 'Symbol',
        title: 'Symbol',
        width: 80,
      },
      {
        type: 'side',
        field: 'Side',
      },
      {
        type: 'size',
        field: 'OrderQty',
        title: 'Order Qty',
        params: {
          currencyField: 'Currency',
        } satisfies SizeColumnParams<ImportedCareOrder>,
      },
      {
        type: 'text',
        field: 'Group',
        width: 100,
      },
      {
        type: 'text',
        field: 'Annotations.ETP.InitiatingFrom',
        title: 'Initiating From',
        width: 140,
      },
      {
        type: 'text',
        field: 'Counterparty',
        title: 'Counterparty',
        width: 140,
      },
    ] satisfies ColumnDef<ImportedCareOrder>[];
  }, [onClickAction]);

  return columns;
};

export const useCareOrderImportColumnsUnqork = ({ onClickAction }: UseCareOrderImportColumnsParams) => {
  const columns: Column[] = useMemo(() => {
    return [
      getActionColumn(onClickAction),
      {
        type: 'text',
        field: 'Annotations.BasketOrder.BasketSymbol',
        title: 'Fund',
        width: 80,
      },
      {
        type: 'number',
        field: 'units',
        title: 'Units',
        width: 80,
      },
      {
        type: 'side',
        field: 'Side',
      },
      {
        type: 'text',
        field: 'Group',
        description: 'Unqork order property orderNumber',
        width: 100,
      },
      {
        type: 'text',
        field: 'Annotations.ETP.InitiatingFrom',
        title: 'Initiating From',
        description: 'Unqork order property liquidityProvider',
        width: 140,
      },
      {
        type: 'text',
        field: 'Counterparty',
        title: 'Counterparty',
        description: 'Unqork Order property authorizedParticipant',
        width: 140,
      },
      {
        type: 'date',
        title: 'Settlement Date',
        field: 'SettleDate',
        params: {
          customFormatter: formattedUTCDay,
        },
      },
    ] satisfies ColumnDef<ImportedCareOrder>[];
  }, [onClickAction]);

  return columns;
};

type ActionColumnValue = ImportCareOrderAction | undefined;

function getActionColumn(
  onClickAction: UseCareOrderImportColumnsParams['onClickAction']
): ColumnDef<ImportedCareOrder> {
  return {
    type: 'custom',
    id: 'action',
    width: 120,
    title: 'Action',
    params: {
      cellRenderer: ({ node }: ICellRendererParams<ImportedCareOrder, ActionColumnValue>) => {
        const row = node.data;
        if (!row) {
          return '';
        }

        return <ActionButton row={row} onClick={() => onClickAction(row)} />;
      },
    },
  };
}

const ActionButton = ({ row, onClick }: { row: ImportedCareOrder; onClick: () => void }) => {
  return (
    <HStack gap="spacing4">
      <Tooltip tooltip={ACTION_METADATA[row.action.type].tooltipInfo}>
        <Text>{ACTION_METADATA[row.action.type].label}</Text>
      </Tooltip>

      {ACTION_METADATA[row.action.type].showOpenActionDialogButton && (
        <IconButton
          size={FormControlSizes.Tiny}
          icon={IconName.ExternalLink}
          onClick={onClick}
          data-testid="import-action-button"
        />
      )}
    </HStack>
  );
};
